import * as React from "react";
import {
  formSet,
  formSetValue,
  useCpFormAnyValue,
  useCpFormError,
  useCpFormSimulateTouched,
  useCpFormTouched,
  useCpFormValue,
  useCpFormWarning,
} from "./../utils";
import { store } from "../../../../store";
import { useCpFormContext } from "../../../../components/form/useCpFormContext";
import { KindSelect, KindSelectProps, KindSelectState } from "../../../../components/antd/kindselect/KindSelect";

interface Props extends Omit<KindSelectProps, "id" | "name" | "value" | "status" | "onChange" | "onStateChange"> {
  name: string;
}

export const CpKindSelect = (props: Props): React.ReactElement => {
  const { id } = useCpFormContext();
  const { name, onFocus, onBlur, ...kindSelectProps } = props;

  const fieldStatePath = React.useMemo(() => `fieldStates.${name}`, [name]);

  const value = useCpFormValue(`${name}.value`);

  const handleChange = React.useCallback(
    (value: string) => {
      const form = store.getState().form.instances[id];

      formSetValue(id, `${name}.value`, value);

      formSet(id, `${fieldStatePath}.modified`, true);

      const onChangeCounter = form.meta.onChangeCounter;
      formSet(id, `meta.onChangeCounter`, onChangeCounter + 1);
    },
    [name],
  );

  const handleStateChange = React.useCallback(
    ({ link, item }: KindSelectState) => {
      formSetValue(id, `${name}.link`, link);
      formSetValue(id, `${name}.item`, item);
    },
    [name],
  );

  const submitted = useCpFormAnyValue(`meta.submitted`);
  const simulateTouched = useCpFormSimulateTouched(name);
  const touched = useCpFormTouched(name);
  const error = useCpFormError(name);
  const warning = useCpFormWarning(name);

  const canShowErrorWarningStatus = React.useMemo(() => {
    return submitted || simulateTouched || touched;
  }, [submitted, simulateTouched, touched]);

  return (
    <KindSelect
      {...kindSelectProps}
      id={name}
      onStateChange={handleStateChange}
      value={value}
      onChange={handleChange}
      status={canShowErrorWarningStatus ? (error ? "error" : warning ? "warning" : undefined) : undefined}
      onFocus={() => {
        formSet(id, `${fieldStatePath}.focused`, true);
        formSet(id, `${fieldStatePath}.visited`, true);
      }}
      onBlur={() => {
        formSet(id, `${fieldStatePath}.focused`, false);
        formSet(id, `${fieldStatePath}.touched`, true);

        const form = store.getState().form.instances[id];
        const onBlurCounter = form.meta.onBlurCounter;
        formSet(id, `meta.onBlurCounter`, onBlurCounter + 1);
      }}
    />
  );
};
